import * as Sentry from '@sentry/react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/atoms/button';

interface FallbackProps {
  error: Error;
  resetError: () => void;
}

function ErrorFallback({ error, resetError }: FallbackProps) {
  return (
    <div
      role="alert"
      className="flex min-h-screen flex-col items-center justify-center gap-4 p-6 text-center"
    >
      <AlertTriangle className="text-destructive h-12 w-12" aria-hidden="true" />
      <h1 className="text-xl font-semibold">Something went wrong</h1>
      <p className="text-muted-foreground max-w-md text-sm">
        An unexpected error occurred. The issue has been reported automatically.
      </p>
      {import.meta.env.DEV && (
        <pre className="bg-muted max-w-lg overflow-auto rounded p-4 text-left text-xs">
          {error.message}
          {'\n'}
          {error.stack}
        </pre>
      )}
      <Button onClick={resetError} variant="outline" className="gap-2">
        <RefreshCw className="h-4 w-4" aria-hidden="true" />
        Try again
      </Button>
    </div>
  );
}

/**
 * Top-level Sentry error boundary.
 * Catches unhandled React render errors, sends them to Sentry,
 * and shows a friendly fallback instead of a blank screen.
 */
export const AppErrorBoundary = Sentry.withErrorBoundary(
  ({ children }: { children: React.ReactNode }) => <>{children}</>,
  {
    fallback: ({ error, resetError }) => (
      <ErrorFallback error={error as Error} resetError={resetError} />
    ),
    showDialog: false,
  },
);
